import React, { useEffect, useState } from "react";
import { X, Trash2, Plus, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

const TierBenefitsModal = ({ tier, onClose }) => {
  const { baseurl, token, setIsUnauthorised ,AdminId} = useGlobalContext();

  const [benefits, setBenefits] = useState([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [loading, setLoading] = useState(false); // list loading
  const [submitting, setSubmitting] = useState(false); // add benefit
  const [deletingId, setDeletingId] = useState(null); // delete state

  const tierId = tier?.tierId || tier?.id || "";

  // Fetch all benefits and pick current tier's benefits
  const fetchBenefits = async () => {
    setLoading(true);
    try {
      // Backend currently ignores payload, but we still send tierId for future-proofing
      const payload = tierId ? { tierId ,adminId:AdminId } : {adminId:AdminId};

      const response = await axios.post(
        `${baseurl}/api/Driver/v1.0/getAllTierBenefitDetails`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      if (response.data.responseCode === "00") {
        const dataArray = response.data.data || [];

        // shape: [{ tierId: "1", benefits: [...] }, ...]
        let currentTierBenefits = [];
        if (tierId) {
          const match = dataArray.find(
            (item) => String(item.tierId) === String(tierId)
          );
          currentTierBenefits = match?.benefits || [];
        } else {
          currentTierBenefits = dataArray.flatMap(
            (item) => item.benefits || []
          );
        }

        setBenefits(currentTierBenefits);
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          response.data.responseMessage || "Failed to fetch tier benefits"
        );
      }
    } catch (error) {
      toast.error("Error fetching tier benefits");
    } finally {
      setLoading(false);
    }
  };

  // Add new benefit
  const handleAddBenefit = async (e) => {
    e.preventDefault();
    if (!newBenefit.trim()) {
      toast.error("Please enter a benefit");
      return;
    }
    if (!tierId) {
      toast.error("Tier ID is missing");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        tierId: String(tierId),
        benefit: newBenefit.trim(),adminId:AdminId
      };

      const response = await axios.post(
        `${baseurl}/api/Driver/v1.0/insertTierBenefit`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      if (response.data.responseCode === "00") {
        toast.success(response.data.responseMessage || "Benefit added");
        setNewBenefit("");
        fetchBenefits();
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(response.data.responseMessage || "Failed to add benefit");
      }
    } catch (error) {
      toast.error("Error adding benefit");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete benefit
  const handleDeleteBenefit = async (benefitId) => {
    if (!benefitId) return;
    setDeletingId(benefitId);

    try {
      const payload = {
        benefitId: String(benefitId),adminId:AdminId
      };

      const response = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteTierBenefit`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      if (response.data.responseCode === "00") {
        toast.success(response.data.responseMessage || "Benefit deleted");
        setBenefits((prev) =>
          prev.filter(
            (benefit) => String(benefit.benefitId) !== String(benefitId)
          )
        );
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(response.data.responseMessage || "Failed to delete benefit");
      }
    } catch (error) {
      toast.error("Error deleting benefit");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (tier) {
      fetchBenefits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-11/12 max-w-2xl rounded-xl bg-slate-50 shadow-2xl border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 bg-slate-100 rounded-t-xl">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {tier?.tier_title || "Tier Details"}
            </h3>
            {tier?.tier_description && (
              <p className="mt-1 text-sm text-slate-600">
                {tier.tier_description}
              </p>
            )}
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-200 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Add Benefit Form */}
          <form
            onSubmit={handleAddBenefit}
            className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold tracking-wide text-slate-800 uppercase">
                Add Benefit
              </h4>
              <span className="text-xs font-medium text-slate-500">
                Tier ID: {tierId || "-"}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                placeholder="Describe the benefit for this tier"
                className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800 shadow-inner focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Benefits List */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold tracking-wide text-slate-800 uppercase">
                Benefits ({benefits.length})
              </h4>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                <span className="ml-2 text-sm text-slate-600">
                  Loading benefits...
                </span>
              </div>
            ) : benefits.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                No benefits added for this tier yet. Use the form above to add
                the first one.
              </div>
            ) : (
              <ul className="space-y-2">
                {benefits.map((benefit) => (
                  <li
                    key={benefit.benefitId}
                    className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-[3px] h-2 w-2 rounded-full bg-slate-500" />
                      <span>{benefit.benefit}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteBenefit(benefit.benefitId)}
                      disabled={deletingId === benefit.benefitId}
                      className="inline-flex items-center rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {deletingId === benefit.benefitId ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="mr-1 h-3 w-3" />
                      )}
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-slate-200 bg-slate-100 px-5 py-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TierBenefitsModal;
